import ArrowLeft from '@/assets/icons/ArrowLeft.svg';
import ArrowRight from '@/assets/icons/ArrowRight.svg';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { buildCloudflareImageUrl } from '@/utils/buildCloudflareImageUrl';
import { useMemo } from 'react';
import { CarouselItem } from 'react-aria-carousel';
import * as Styled from './ContentCarousel.styled';
import type { ContentCarouselData, ContentCarouselSlide } from './types';

const Slide = ({ slide }: { slide: ContentCarouselSlide }) => {
  // Stable object identity. React diffs dangerouslySetInnerHTML by reference, so a
  // fresh literal re-assigns innerHTML on every commit and replaces the text nodes
  // inside the carousel, which trips react-aria-carousel's MutationObserver — it
  // calls hasAttribute() on every added/removed node, and Text nodes have none.
  const descriptionHtml = useMemo(
    () => (slide.description ? { __html: slide.description } : undefined),
    [slide.description],
  );

  if (!slide.imageUrl) {
    return null;
  }

  const img = (
    <Styled.ImageBox>
      <Styled.Img
        src={buildCloudflareImageUrl(slide.imageUrl, { width: 600 })}
        alt={slide.imageAlt ?? ''}
        loading="lazy"
        decoding="async"
      />
    </Styled.ImageBox>
  );

  const title = slide.title ? <Styled.Title>{slide.title}</Styled.Title> : null;

  const description = descriptionHtml ? (
    <Styled.Description dangerouslySetInnerHTML={descriptionHtml} />
  ) : null;

  const textAlign = slide.textAlignment === 'pull-center'
    ? 'center' as const
    : slide.textAlignment === 'pull-right'
      ? 'right' as const
      : undefined;

  const textStyle = textAlign ? { textAlign } : undefined;

  return (
    <Styled.Card>
      {slide.linkUrl ? (
        <Styled.ImageLink
          href={slide.linkUrl}
          aria-label={slide.title || slide.imageAlt || undefined}
        >
          {img}
        </Styled.ImageLink>
      ) : (
        img
      )}
      <div style={textStyle}>
        {title}
        {description}
      </div>
    </Styled.Card>
  );
};

export const ContentCarousel = ({ data }: { data: ContentCarouselData }) => {
  const isWideScreen = useMediaQuery('(width >= 64rem)');
  const isTablet = useMediaQuery('(width >= 48rem)');

  const slides = data.slides ?? [];

  if (slides.length === 0) {
    return null;
  }

  const getItemsPerPage = () => {
    if (isWideScreen) return Math.min(data.desktopSlides, slides.length);
    if (isTablet) return Math.min(data.tabletSlides, slides.length);
    return Math.min(1.5, slides.length);
  };

  const itemsPerPage = getItemsPerPage();
  const enableScrolling = slides.length > itemsPerPage;
  const showArrows = data.showArrows && isWideScreen && enableScrolling;

  return (
    <Styled.Carousel
      key={data.key}
      aria-label="Content carousel"
      itemsPerPage={itemsPerPage}
      loop={enableScrolling ? 'native' : false}
      scrollPadding="5px"
      spaceBetweenItems={isWideScreen ? '12px' : '14px'}
      autoplay={data.autoPlay && enableScrolling}
      autoplayInterval={data.autoPlay ? 4000 : undefined}
    >
      <Styled.Track $scroll={enableScrolling}>
        {slides.map((slide, index) => (
          <CarouselItem key={`${data.key}-${index}`}>
            <Slide slide={slide} />
          </CarouselItem>
        ))}
      </Styled.Track>

      {showArrows ? (
        <>
          <Styled.Arrow dir="prev" data-direction="prev" aria-label="Previous slides">
            <ArrowLeft aria-hidden="true" />
          </Styled.Arrow>
          <Styled.Arrow dir="next" data-direction="next" aria-label="Next slides">
            <ArrowRight aria-hidden="true" />
          </Styled.Arrow>
        </>
      ) : null}
    </Styled.Carousel>
  );
};
